import { useMemo } from 'react';
import { filterConditionalSections, getTotalVisibleSubsections, Section, Subsection, Answers } from '../utils/conditionalLogic';

type SetStateAction = React.Dispatch<React.SetStateAction<number>>;

interface UseAssessmentNavigationReturn {
  getCurrentSubsection: () => Subsection | null;
  getCurrentSubsectionGlobalIndex: () => number;
  getTotalSubsections: () => number;
  goToNextSubsection: (setCurrentSectionIndex: SetStateAction, setCurrentSubsectionIndex: SetStateAction) => void;
  goToPreviousSubsection: (setCurrentSectionIndex: SetStateAction, setCurrentSubsectionIndex: SetStateAction) => void;
  isLastSubsection: () => boolean;
  isFirstSubsection: () => boolean;
  goToSubsection: (sectionIndex: number, subsectionIndex: number, setCurrentSectionIndex: SetStateAction, setCurrentSubsectionIndex: SetStateAction) => void;
  filteredQuestionnaire: Section[];
}

export const useAssessmentNavigation = (
  questionnaire: Section[] | null,
  currentSectionIndex: number,
  currentSubsectionIndex: number,
  answers: Answers = {}
): UseAssessmentNavigationReturn => {
  // Filter questionnaire based on conditional logic
  const filteredQuestionnaire = useMemo(() => {
    return filterConditionalSections(questionnaire, answers);
  }, [questionnaire, answers]);

  const getCurrentSubsection = () => {
    if (!filteredQuestionnaire?.[currentSectionIndex]) return null;
    return filteredQuestionnaire[currentSectionIndex].sections?.[currentSubsectionIndex] || null;
  };

  const getCurrentSubsectionGlobalIndex = () => {
    if (!filteredQuestionnaire) return 0;
    return filteredQuestionnaire.slice(0, currentSectionIndex).reduce((acc, section) => acc + (section.sections?.length || 0), 0) + currentSubsectionIndex;
  };

  const getTotalSubsections = () => {
    if (!filteredQuestionnaire) return 0;
    return getTotalVisibleSubsections(questionnaire, answers);
  };

  const goToNextSubsection = (setCurrentSectionIndex: SetStateAction, setCurrentSubsectionIndex: SetStateAction) => {
    if (!filteredQuestionnaire) return;

    const currentSection = filteredQuestionnaire[currentSectionIndex];
    const nextSubsectionIndex = currentSubsectionIndex + 1;

    if (nextSubsectionIndex < (currentSection.sections?.length || 0)) {
      setCurrentSubsectionIndex(nextSubsectionIndex);
    } else if (currentSectionIndex < filteredQuestionnaire.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      setCurrentSubsectionIndex(0);
    }
  };

  const goToPreviousSubsection = (setCurrentSectionIndex: SetStateAction, setCurrentSubsectionIndex: SetStateAction) => {
    if (!filteredQuestionnaire) return;

    if (currentSubsectionIndex > 0) {
      setCurrentSubsectionIndex(currentSubsectionIndex - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      setCurrentSubsectionIndex(filteredQuestionnaire[currentSectionIndex - 1].sections?.length - 1 || 0);
    }
  };

  const isLastSubsection = () => {
    if (!filteredQuestionnaire) return false;
    return currentSectionIndex === filteredQuestionnaire.length - 1 &&
           currentSubsectionIndex === (filteredQuestionnaire[currentSectionIndex].sections?.length || 1) - 1;
  };

  const isFirstSubsection = () => {
    return currentSectionIndex === 0 && currentSubsectionIndex === 0;
  };

  const goToSubsection = (sectionIndex: number, subsectionIndex: number, setCurrentSectionIndex: SetStateAction, setCurrentSubsectionIndex: SetStateAction) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentSubsectionIndex(subsectionIndex);
  };

  return {
    getCurrentSubsection,
    getCurrentSubsectionGlobalIndex,
    getTotalSubsections,
    goToNextSubsection,
    goToPreviousSubsection,
    isLastSubsection,
    isFirstSubsection,
    goToSubsection,
    filteredQuestionnaire // Export filtered questionnaire for use in progress indicators
  };
};

export default useAssessmentNavigation;
